/**
 * ce-search full results page (/search). Drives the results grid, sort, facet
 * sidebar, and pagination entirely from the ce-search hybrid service so the
 * page ranks identically to the nav dropdown. Renders real theme product cards
 * (meta-pattern-product-card, server-rendered) like the nav integration.
 *
 * ce-search is the default search experience (ABT-237 concluded; arm b won).
 * Gating is Liquid-side: this script only loads and #ce-search-results only
 * renders when settings.ce_search_api_url is set — clearing that theme
 * setting reverts everyone to native search.
 */
var ceSearchResultsMounted = false;
function ceSearchResultsInit() {
  if (ceSearchResultsMounted) return;
  var root = document.getElementById("ce-search-results");
  if (!root) return;
  ceSearchResultsMounted = true;
  var API = root.dataset.api.replace(/\/$/, "");
  var CARD_SECTION = "meta-pattern-product-card";
  var PAGE_SIZE = 24;

  var grid = document.getElementById("ce-grid");
  var facetsEl = document.getElementById("ce-facets");
  var sortSel = document.getElementById("ce-sort");
  var countEl = document.getElementById("ce-count");
  var loadMore = document.getElementById("ce-load-more");
  var activeEl = document.getElementById("ce-active-filters");
  var linksEl = document.getElementById("ce-links");

  // Facet field -> sidebar label. Order here is the render order.
  var FACETS = [
    { field: "product_type", label: "Category" },
    { field: "option_color", label: "Color" },
    { field: "option_size", label: "Size" },
    { field: "available", label: "Availability", map: { true: "In stock", false: "Sold out" } },
  ];
  var SORTS = [
    { value: "relevance", label: "Relevance" },
    { value: "best_selling", label: "Best Sellers" },
    { value: "price_asc", label: "Price (Low to High)" },
    { value: "price_desc", label: "Price (High to Low)" },
  ];

  // ---- state (mirrored to the URL) ----
  var state = { q: "", sort: "relevance", selected: {}, priceMin: null, priceMax: null, page: 1 };
  var bounds = null; // { min, max } from the API, once known
  var controller = null;
  var firstLoad = true;

  function esc(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }
  function money(n) {
    var s = Number(n).toLocaleString("en-US", { style: "currency", currency: "USD" });
    return s.replace(/\.00$/, "");
  }

  // ---- session + click analytics (mirrors ce-search-nav.js) ----
  function sessionId() {
    try {
      var sid = sessionStorage.getItem("ce_sid");
      if (!sid) {
        sid =
          window.crypto && crypto.randomUUID
            ? crypto.randomUUID()
            : "sid-" + Date.now() + "-" + Math.random().toString(36).slice(2);
        sessionStorage.setItem("ce_sid", sid);
      }
      return sid;
    } catch (e) {
      return "";
    }
  }

  // Order attribution: stamp the ce-search session id onto the cart as the
  // ce_search_session note attribute so it rides through to the order and the
  // orders/create webhook can join it back to searches. Once per session,
  // fire-and-forget - attribution must never affect search.
  function stampCartSession() {
    try {
      var sid = sessionId();
      if (!sid || sessionStorage.getItem("ce_sid_stamped")) return;
      sessionStorage.setItem("ce_sid_stamped", "1");
      fetch("/cart/update.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ attributes: { ce_search_session: sid } }),
      }).catch(function () {
        try {
          sessionStorage.removeItem("ce_sid_stamped");
        } catch (e) {}
      });
    } catch (e) {
      /* storage blocked: skip attribution */
    }
  }

  function trackClick(hit, position) {
    try {
      navigator.sendBeacon(
        API + "/v1/analytics/click",
        new Blob(
          [
            JSON.stringify({
              query: state.q,
              product_id: String(hit.id),
              position: position,
              session_id: sessionId() || undefined,
            }),
          ],
          // text/plain is CORS-safelisted: no preflight, so the beacon survives
          // the click navigating away (a JSON-typed beacon is dropped after it).
          { type: "text/plain;charset=UTF-8" }
        )
      );
    } catch (e) {
      /* never break navigation */
    }
  }

  // ---- URL <-> state ----
  function readUrl() {
    var p = new URLSearchParams(location.search);
    state.q = p.get("q") || "";
    state.sort = p.get("sort") || "relevance";
    state.priceMin = p.get("price_min") ? Number(p.get("price_min")) : null;
    state.priceMax = p.get("price_max") ? Number(p.get("price_max")) : null;
    state.selected = {};
    FACETS.forEach(function (f) {
      var raw = p.get(f.field);
      if (raw) state.selected[f.field] = raw.split("|").filter(Boolean);
    });
  }
  function writeUrl() {
    var p = new URLSearchParams();
    if (state.q) p.set("q", state.q);
    p.set("type", "product");
    if (state.sort && state.sort !== "relevance") p.set("sort", state.sort);
    FACETS.forEach(function (f) {
      var v = state.selected[f.field];
      if (v && v.length) p.set(f.field, v.join("|"));
    });
    if (state.priceMin != null) p.set("price_min", String(state.priceMin));
    if (state.priceMax != null) p.set("price_max", String(state.priceMax));
    history.replaceState(null, "", location.pathname + "?" + p.toString());
  }

  // ---- Typesense filter_by from selected facets + price ----
  function tsValue(v) {
    return "`" + String(v).replace(/`/g, "") + "`";
  }
  function buildFilters() {
    var parts = [];
    FACETS.forEach(function (f) {
      var vals = state.selected[f.field];
      if (vals && vals.length) {
        parts.push(f.field + ":=[" + vals.map(tsValue).join(",") + "]");
      }
    });
    if (state.priceMin != null) parts.push("price_min:>=" + state.priceMin);
    if (state.priceMax != null) parts.push("price_min:<=" + state.priceMax);
    return parts.join(" && ");
  }

  // ---- card rendering (mirrors ce-search-nav.js) ----
  function ensureCardScript(doc) {
    if (window.__productCardScriptReady) return Promise.resolve();
    if (window.__productCardScriptPromise) return window.__productCardScriptPromise;

    var tag = doc.querySelector('script[src*="pattern__product-card.js"]');
    if (!tag) return Promise.reject(new Error("product card script not found"));

    window.__productCardScriptPromise = new Promise(function (resolve, reject) {
      var script = document.createElement("script");
      script.src = tag.getAttribute("src");
      script.addEventListener("load", resolve, { once: true });
      script.addEventListener(
        "error",
        function () {
          window.__productCardScriptPromise = null;
          reject(new Error("product card script failed to load"));
        },
        { once: true },
      );
      document.head.appendChild(script);
    });

    return window.__productCardScriptPromise;
  }
  function fetchCard(hit, signal) {
    var params = "section_id=" + CARD_SECTION;
    if (hit.matched_variant) params += "&variant=" + encodeURIComponent(hit.matched_variant.id);
    return fetch("/products/" + encodeURIComponent(hit.handle) + "?" + params, { signal: signal })
      .then(function (res) {
        if (res.status === 404) return null; // unpublished since last sync — drop
        if (!res.ok) throw new Error("section " + res.status);
        return res.text();
      })
      .then(function (html) {
        if (html === null) return null;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var card = doc.querySelector("article");
        if (!card) return null;
        return ensureCardScript(doc).then(function () {
          return card.outerHTML;
        });
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") throw err;
        return null; // a missing card drops out rather than breaking the grid
      });
  }

  // ---- facet sidebar ----
  function renderFacets(facetCounts) {
    // Collapsible accordion toggle (+ / −), matching the native filter menu.
    var TOGGLE =
      '<span class="ml-2 shrink-0 transition group-open:rotate-180">' +
      '<svg class="block size-4" fill="none" stroke="currentColor" viewBox="0 0 11 6" width="11" height="6" aria-hidden="true"><path d="M1 1L5.47304 4.97604L9.94608 1" stroke="currentColor" stroke-width="0.75"/></svg>' +
      "</span>";
    var SUMMARY_CLS = "flex cursor-pointer items-center justify-between py-4 text-sm md:text-base text-ce-gray-800";
    var byField = {};
    (facetCounts || []).forEach(function (fc) {
      byField[fc.field_name] = fc.counts || [];
    });

    var html = FACETS.map(function (f) {
      var counts = byField[f.field];
      if (!counts || !counts.length) return "";
      var selected = state.selected[f.field] || [];
      var rows = counts
        .map(function (c) {
          var label = f.map && f.map[c.value] != null ? f.map[c.value] : c.value;
          var checked = selected.indexOf(String(c.value)) !== -1 ? " checked" : "";
          return (
            '<label class="flex items-center gap-2 py-1 text-sm cursor-pointer">' +
            '<input type="checkbox" class="search-filters-checkbox" data-facet="' +
            f.field +
            '" value="' +
            esc(c.value) +
            '"' +
            checked +
            ">" +
            '<span class="flex-1">' +
            esc(label) +
            "</span>" +
            '<span class="text-ce-gray-500">' +
            c.count +
            "</span>" +
            "</label>"
          );
        })
        .join("");
      return (
        '<details class="group border-b border-ce-cool">' +
        '<summary class="' +
        SUMMARY_CLS +
        '"><span>' +
        esc(f.label) +
        "</span>" +
        TOGGLE +
        "</summary>" +
        '<div class="pb-4">' +
        rows +
        "</div></details>"
      );
    }).join("");

    // price range
    if (bounds && bounds.max > bounds.min) {
      var lo = state.priceMin != null ? state.priceMin : bounds.min;
      var hi = state.priceMax != null ? state.priceMax : bounds.max;
      html +=
        '<details class="group border-b border-ce-cool">' +
        '<summary class="' +
        SUMMARY_CLS +
        '"><span>Price</span>' +
        TOGGLE +
        "</summary>" +
        '<div class="flex items-center gap-2 pb-4 text-sm">' +
        '<input id="ce-price-min" type="number" inputmode="numeric" min="' +
        bounds.min +
        '" max="' +
        bounds.max +
        '" value="' +
        lo +
        '" class="w-20 border border-ce-cool px-2 py-1" aria-label="Minimum price">' +
        "<span>to</span>" +
        '<input id="ce-price-max" type="number" inputmode="numeric" min="' +
        bounds.min +
        '" max="' +
        bounds.max +
        '" value="' +
        hi +
        '" class="w-20 border border-ce-cool px-2 py-1" aria-label="Maximum price">' +
        '<button id="ce-price-apply" type="button" class="ml-1 underline">Apply</button>' +
        "</div></details>";
    }
    facetsEl.innerHTML = html;

    facetsEl.querySelectorAll("input[data-facet]").forEach(function (cb) {
      cb.addEventListener("change", function () {
        var field = cb.dataset.facet,
          val = cb.value;
        var arr = state.selected[field] || (state.selected[field] = []);
        if (cb.checked) {
          if (arr.indexOf(val) === -1) arr.push(val);
        } else {
          state.selected[field] = arr.filter(function (v) {
            return v !== val;
          });
        }
        state.page = 1;
        run();
      });
    });
    var apply = document.getElementById("ce-price-apply");
    if (apply) {
      apply.addEventListener("click", function () {
        var mn = Number(document.getElementById("ce-price-min").value);
        var mx = Number(document.getElementById("ce-price-max").value);
        state.priceMin = bounds && mn > bounds.min ? mn : null;
        state.priceMax = bounds && mx < bounds.max ? mx : null;
        state.page = 1;
        run();
      });
    }
  }

  // active filter chips
  function renderActive() {
    if (!activeEl) return;
    var chips = [];
    FACETS.forEach(function (f) {
      (state.selected[f.field] || []).forEach(function (v) {
        var label = f.map && f.map[v] != null ? f.map[v] : v;
        chips.push(
          '<button type="button" class="flex items-center justify-between gap-2 rounded-lg bg-ce-gray-100 p-2" data-clear-facet="' +
            f.field +
            '" data-clear-val="' +
            esc(v) +
            '">' +
            esc(label) +
            " &times;</button>"
        );
      });
    });
    if (state.priceMin != null || state.priceMax != null) {
      var pl =
        (state.priceMin != null ? money(state.priceMin) : money(bounds ? bounds.min : 0)) +
        "–" +
        (state.priceMax != null ? money(state.priceMax) : money(bounds ? bounds.max : 0));
      chips.push(
        '<button type="button" class="flex items-center justify-between gap-2 rounded-lg bg-ce-gray-100 p-2" data-clear-price="1">' +
          pl +
          " &times;</button>"
      );
    }
    if (chips.length)
      chips.push('<button type="button" class="underline text-sm" data-clear-all="1">Clear All</button>');
    activeEl.innerHTML = chips.join("");
    activeEl.querySelectorAll("[data-clear-facet]").forEach(function (b) {
      b.addEventListener("click", function () {
        var f = b.dataset.clearFacet,
          v = b.dataset.clearVal;
        state.selected[f] = (state.selected[f] || []).filter(function (x) {
          return x !== v;
        });
        state.page = 1;
        run();
      });
    });
    var pc = activeEl.querySelector("[data-clear-price]");
    if (pc)
      pc.addEventListener("click", function () {
        state.priceMin = state.priceMax = null;
        state.page = 1;
        run();
      });
    var ca = activeEl.querySelector("[data-clear-all]");
    if (ca)
      ca.addEventListener("click", function () {
        state.selected = {};
        state.priceMin = state.priceMax = null;
        state.page = 1;
        run();
      });
  }

  // Quick links (promoted links) for non-product intents like "returns" or
  // "track order" — additive to product results, rendered above the grid.
  function renderLinks(links) {
    if (!linksEl) return;
    if (!links || !links.length) {
      linksEl.innerHTML = "";
      linksEl.style.display = "none";
      return;
    }
    linksEl.style.display = "";
    var anchors = links
      .map(function (l) {
        return (
          '<a href="' +
          esc(l.url) +
          '" class="text-sm font-medium text-ce-gray-800 underline underline-offset-2 hover:text-ce-gray-700">' +
          esc(l.title) +
          " &rarr;</a>"
        );
      })
      .join('<span class="text-ce-gray-700" aria-hidden="true">&middot;</span>');
    linksEl.innerHTML =
      '<div class="flex flex-wrap items-center gap-x-4 gap-y-2 rounded border border-ce-gray-300 bg-ce-white-200 px-4 py-3 font-work-sans">' +
      '<span class="text-sm text-ce-gray-700">Looking for</span>' +
      anchors +
      "</div>";
  }

  // Section HTML defers every extra image (card_index is omitted, so it is
  // not 0). The first result is the only card whose swipe hint runs, and
  // only on touch — promote its second slide to a real eager image here.
  function prepareSearchCard(html, index, promoteSecondary) {
    try {
      var doc = new DOMParser().parseFromString(html, "text/html");
      var article = doc.querySelector("article");
      if (!article) return html;
      article.setAttribute("data-card-index", String(index));
      if (promoteSecondary) {
        var tpl = article.querySelector('template[data-deferred-card-media="secondary"]');
        if (tpl && tpl.content) {
          var img = tpl.content.querySelector("img");
          if (img) {
            img.setAttribute("loading", "eager");
            img.setAttribute("fetchpriority", "low");
          }
          tpl.replaceWith(tpl.content);
        }
      }
      return article.outerHTML;
    } catch (e) {
      return html;
    }
  }

  function renderGrid(hits, cards, append) {
    // Absolute position across pages so "Load More" results report true rank.
    var base = append ? grid.querySelectorAll("li[data-ce-pos]").length : 0;
    var touchPeek = window.matchMedia("(hover: none) and (pointer: coarse)").matches;
    var items = cards
      .map(function (html, i) {
        var index = base + i;
        var prepared = prepareSearchCard(html, index, !append && i === 0 && touchPeek);
        return '<li data-ce-pos="' + (index + 1) + '">' + prepared + "</li>";
      })
      .join("");
    // Cards stream in ahead of the Liquid skeletons so the grid doesn't jump.
    var skeleton = grid.querySelector("li[data-ce-skeleton]");
    if (skeleton) skeleton.insertAdjacentHTML("beforebegin", items);
    else if (append) grid.insertAdjacentHTML("beforeend", items);
    else grid.innerHTML = items;

    grid.querySelectorAll("li[data-ce-pos]:not([data-wired])").forEach(function (li, i) {
      li._ceHit = hits[i]; // hit object for click tracking
      li.setAttribute("data-wired", "1");
      li.addEventListener(
        "click",
        function () {
          if (li._ceHit) trackClick(li._ceHit, parseInt(li.dataset.cePos, 10));
        },
        { capture: true }
      );
    });
  }

  function run() {
    if (controller) controller.abort();
    var ctrl = (controller = new AbortController());
    writeUrl();
    root.setAttribute("aria-busy", "true");

    var append = state.page > 1;
    var params = new URLSearchParams({
      q: state.q,
      sort: state.sort,
      page: String(state.page),
      limit: String(PAGE_SIZE),
      facets: "1",
      log: firstLoad ? "1" : "0",
    });
    var f = buildFilters();
    if (f) params.set("filters", f);
    var sid = sessionId();
    if (sid) params.set("session_id", sid);
    firstLoad = false;
    stampCartSession();

    fetch(API + "/v1/search?" + params.toString(), { signal: ctrl.signal })
      .then(function (res) {
        return res.json();
      })
      .then(function (data) {
        if (ctrl.signal.aborted) return;
        bounds = data.price_bounds || bounds;
        if (countEl) countEl.textContent = data.found + (data.found === 1 ? " Result" : " Results");
        // only (re)render facets/links on a fresh query/filter change, not page append
        if (!append) renderFacets(data.facets);
        if (!append) renderLinks(data.links);
        renderActive();

        var hits = data.hits || [];
        if (!hits.length && !append) {
          grid.innerHTML =
            '<li style="grid-column:1/-1"><p class="text-md text-ce-gray-700 py-10 text-center">No results for &ldquo;' +
            esc(state.q) +
            "&rdquo;. Try a different term.</p></li>";
          if (loadMore) loadMore.style.display = "none";
          root.removeAttribute("aria-busy");
          return;
        }
        // Show cards as they arrive instead of waiting on the slowest of the
        // batch: whenever the next card(s) in rank order are ready, flush them.
        var ready = new Array(hits.length);
        var next = 0;
        var flushed = 0;
        function flush() {
          if (ctrl.signal.aborted) return;
          var keptHits = [],
            keptCards = [];
          while (next < hits.length && ready[next] !== undefined) {
            if (ready[next]) {
              keptHits.push(hits[next]);
              keptCards.push(ready[next]);
            }
            next++;
          }
          if (!keptHits.length) return;
          renderGrid(keptHits, keptCards, append || flushed > 0);
          flushed += keptHits.length;
        }
        return Promise.all(
          hits.map(function (h, i) {
            return fetchCard(h, ctrl.signal).then(function (card) {
              ready[i] = card || null;
              flush();
            });
          })
        ).then(function () {
          if (ctrl.signal.aborted) return;
          if (!flushed && !append) grid.innerHTML = "";
          grid.querySelectorAll("li[data-ce-skeleton]").forEach(function (li) {
            li.remove();
          });
          if (loadMore) loadMore.style.display = state.page * PAGE_SIZE < data.found ? "" : "none";
          root.removeAttribute("aria-busy");
        });
      })
      .catch(function (err) {
        if (err && err.name === "AbortError") return;
        root.removeAttribute("aria-busy");
        console.warn("ce-search results error", err);
        // Fall back to native search WITHOUT navigating: we are already on
        // /search, so a redirect here would remount this script and loop
        // forever while the API is down. Swap the wrappers directly (the
        // toggle CSS is Liquid-gated on ce_search_api_url and uses
        // !important, so the inline override needs !important too). The
        // native results are server-rendered for the current query.
        if (!append) {
          var ceWrap = document.querySelector(".abt-237-ce-wrapper");
          if (ceWrap) ceWrap.style.display = "none";
          var nativeWrap = document.querySelector(".abt-237-native-wrapper");
          if (nativeWrap) nativeWrap.style.setProperty("display", "block", "important");
        }
      });
  }

  // ---- wire controls ----
  if (sortSel) {
    sortSel.innerHTML = SORTS.map(function (s) {
      return (
        '<option value="' + s.value + '"' + (s.value === state.sort ? " selected" : "") + ">" + s.label + "</option>"
      );
    }).join("");
    sortSel.addEventListener("change", function () {
      state.sort = sortSel.value;
      state.page = 1;
      run();
    });
  }
  if (loadMore)
    loadMore.addEventListener("click", function () {
      state.page += 1;
      run();
    });

  // keep in sync if the shopper edits the page's search box (search.liquid sets ?q=)
  window.addEventListener("popstate", function () {
    readUrl();
    if (sortSel) sortSel.value = state.sort;
    run();
  });

  readUrl();
  if (sortSel) sortSel.value = state.sort;
  run();
}

// search.liquid loads this script after the #ce-search-results markup, so
// mount right away instead of waiting for DOMContentLoaded: the rest of the
// page (footer, third-party scripts) held the search request back ~1s.
// The listener stays as a fallback if the markup isn't there yet.
ceSearchResultsInit();
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", ceSearchResultsInit);
}
// Compose experiments that apply after DOMContentLoaded dispatch this (see
// the JS-trigger example in snippets/compose.liquid).
document.addEventListener("compose-experiment-executed", ceSearchResultsInit);
